import React, { Dispatch, useState } from "react";
import styles from './_portal-appointment.module.scss';
import { isNil, isNotNil } from "ramda";
import {
  IntlProvider,
  load,
  loadMessages,
  LocalizationProvider,
} from '@progress/kendo-react-intl';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import weekData from 'cldr-core/supplemental/weekData.json';
import numbers from 'cldr-numbers-full/main/es/numbers.json';
import caGregorian from 'cldr-dates-full/main/es/ca-gregorian.json';
import dateFields from 'cldr-dates-full/main/es/dateFields.json';
import timeZoneNames from 'cldr-dates-full/main/es/timeZoneNames.json';
import dzVerbiage from './apptDictionary.json';
import { Calendar, CalendarCellProps, CalendarChangeEvent, TimePicker } from "@progress/kendo-react-dateinputs";
import { getWordingForLanguage } from "../../Helpers/LanguageDictionary";
import { CustomerPortalUser } from "../../Models/CustomerPortalUser";
import { createEventString, formatPhoneNumber } from "../../Helpers/Utilities";
import { Loader } from "@progress/kendo-react-indicators";
import { classNames } from '@progress/kendo-react-common';
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { getMobileOperatingSystem } from "../../Helpers/UserAgentHelpers";
import MapIcon from '@mui/icons-material/Map';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {hasValue, DatePickerWithYearSanitizer} from "@amsanalytics/react-shared-library";

load(
  likelySubtags,
  currencyData,
  weekData,
  numbers,
  caGregorian,
  dateFields,
  timeZoneNames
);
loadMessages(dzVerbiage, 'es-ES');

// Custom cell for the calendar
const CustomCell = (props: CalendarCellProps & { minDate: Date, selectedLanguageId: string }) => {
  // sundays and past dates are disabled
  let isDisabled = (props.value <= props.minDate) || props.value.getDay() === 0;
  const isSunday = props.value.getDay() === 0;
  const title = isSunday ? getWordingForLanguage('closedOnSundays', props.selectedLanguageId) : '';

  const handleClick = (ev: any) => {
    if (!isDisabled) {
      if (props.onClick) {
        props.onClick(props.value, ev);
      }
    }
  };

  const style = {
    cursor: isDisabled ? 'no-drop' : 'pointer',
    opacity: isDisabled ? 0.6 : 1,
  };
  const className = classNames({
    'k-calendar-td': true,
    'k-state-selected': props.isSelected,
    'k-state-focused': props.isFocused,
  });
  const titleValue = isDisabled ? title : '';
  const MyContent = (): any => {
    return props.formattedValue;
  };

  return (
    <td onClick={handleClick} className={className} style={style}>
      <span
        onClick={handleClick}
        className={'k-link '}
        style={style}
        title={titleValue}
      >
        <MyContent />
      </span>
    </td>
  );
};

// Custom calendar component
const CustomCalendar = (props: { minDate: Date, selectedLanguageId: string, onChange: (e: CalendarChangeEvent) => void }) => {
  const minDate = props.minDate;
  return (
    <Calendar
      cell={(props: any) => <CustomCell {...props} selectedLanguageId={props.selectedLanguageId} minDate={minDate} />}
      min={minDate}
      onChange={(e: CalendarChangeEvent) => {
        props.onChange(e);
      }}
    />
  );
}

export interface PortalAppointmentProps {
  disptach: Dispatch<any>;
  selectedLanguageId: string;
  user: CustomerPortalUser;
  isSchedulingAppointment: boolean; // will be used to show a spinner while the appointment is being scheduled
  onScheduleAppointment: (date: Date) => void;
  selectedApplicationCustomerId: string;
}

export const PortalAppointment = (props: PortalAppointmentProps) => {
  const selectedLanguageId = props.selectedLanguageId;
  // date option selected by the user
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  // time option selected by the user
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  // whether or not the "configrm appointment" dialog is open
  const [isConfirmingAppointment, setIsConfirmingAppointment] = useState(false);
  // minimum date is yesterday
  const minCalendarDate = new Date(new Date().setDate(new Date().getDate() - 1));
  // constructs the description for the appointment
  const getDescription = (): string => {
    let description: string = `${getWordingForLanguage('apptDescription', props.selectedLanguageId)} ${props.user.assignedEmployeeName} ${getWordingForLanguage('apptDescriptionContinued', props.selectedLanguageId)} ${props.user.assignedEmployeeStoreName}. `;
    if (hasValue(props.user.assignedEmployeeStorePhone)) {
      description += ` ${getWordingForLanguage('apptQuestionsDescription', props.selectedLanguageId)} ${formatPhoneNumber(props.user.assignedEmployeeStorePhone)}.`;
    }
    return description;
  }

  // constructs the 'Confirm Appt' description
  const getConfirmApptDescription = (): string => {
    return `${getWordingForLanguage('confirmApptDesc', props.selectedLanguageId)} ${new Date(selectedDate?.getFullYear() as number, selectedDate?.getMonth() as number, selectedDate?.getDate() as number).toLocaleDateString()} ${getWordingForLanguage('at', props.selectedLanguageId)} ${new Date(selectedDate?.getFullYear() as number, selectedDate?.getMonth() as number, selectedDate?.getDate() as number, selectedTime?.getHours() as number, selectedTime?.getMinutes() as number).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}. ${getWordingForLanguage('confimApptDescContinued', props.selectedLanguageId)}`;
  }

  // constructs the google maps URL based on the user's device
  const getGoogleMapsUrl = (): string => {
    const userAgent = getMobileOperatingSystem();
    if (userAgent === 'iOS') {
      return `https://maps.apple.com/?q=${props.user.assignedEmployeeStoreAddress}`;
    }
    if (userAgent === 'Android') {
      return `https://maps.google.com/?q=${props.user.assignedEmployeeStoreAddress}`;
    }
    return `https://www.google.com/maps?q=${props.user.assignedEmployeeStoreAddress}`;
  }

  // returns true if we should show the 'Open in Maps' link. only show this link if the user has a full store address.
  // Ensure that after splitting the address on a comma, both items are not empty strings
  // const shouldShowOpenInMapsLink = (): boolean => {
  //   return isNotNil(props.user.assignedEmployeeStoreAddress)
  //     && hasValue(props.user.assignedEmployeeStoreAddress)
  //     && props.user.assignedEmployeeStoreAddress.split(',').filter((item: string) => item.trim() !== '').length > 1;
  // }
  return (
    <div className={styles.portalAppointmentContainer}>
      {/* <h3 style={{ marginBottom: 0, marginTop: 0 }}>{isNil(props.user.appointmentDate) ? getWordingForLanguage('scheduleApptTitle', props.selectedLanguageId) : getWordingForLanguage('upcomingApptTitle', props.selectedLanguageId)}</h3> */}
      {props.isSchedulingAppointment !== true && isNil(props.user.appointmentDate) && (
        <div style={{ maxWidth: '400px' }}>
          <p style={{ margin: '5px 5px 1em' }}>{getDescription()}</p>
          <LocalizationProvider
            language={props.selectedLanguageId === '1' ? 'en-US' : 'es-ES'}
          >
            <IntlProvider
              locale={props.selectedLanguageId === '1' ? 'en-US' : 'es-ES'}
            >
              <Tooltip anchorElement="target" position="top">
                <DatePickerWithYearSanitizer
                  placeholder={getWordingForLanguage('pickDate', props.selectedLanguageId)}
                  value={selectedDate}
                  fillMode={'outline'}
                  adaptive={true}
                  min={minCalendarDate}
                  onChange={(e) => {
                    setSelectedDate(e.value);
                  }}
                  calendar={(props: any) => <CustomCalendar {...props} minDate={minCalendarDate} selectedLanguageId={props.selectedLanguageId} onChange={props.onChange} />}
                />
              </Tooltip>
              <div style={{ margin: '10px 0' }}>
                <TimePicker
                  value={selectedTime}
                  onChange={(e) => {
                    setSelectedTime(e.value);
                  }}
                  format={'hh:mm a'}
                  fillMode={'outline'}
                  adaptive={true}
                  placeholder={getWordingForLanguage('pickTime', props.selectedLanguageId)}
                  min={selectedDate?.getDate() === new Date().getDate() ? new Date(new Date().setHours(new Date().getHours(), new Date().getMinutes(), 0, 0)) : new Date(new Date().setHours(8, 0, 0, 0))}
                  max={selectedDate?.getDay() === 6 ? new Date(new Date().setHours(16, 0, 0, 0)) : new Date(new Date().setHours(18, 0, 0, 0))}
                  steps={{ minute: 15 }}
                />
              </div>
            </IntlProvider>
          </LocalizationProvider>
          <Button
            themeColor={'primary'}
            style={{ width: '100%' }}
            disabled={isNil(selectedDate) || isNil(selectedTime)}
            onClick={() => {
              if (isNotNil(selectedDate) && isNotNil(selectedTime)) {
                setIsConfirmingAppointment(true);
              }
            }}
          >
            {getWordingForLanguage('scheduleApptBtn', props.selectedLanguageId)}
          </Button>
        </div>
      )}
      {props.isSchedulingAppointment !== true && isNotNil(props.user.appointmentDate) && (
        <div className={styles.appointmentCard}>
          <div className={styles.appointmentTitle}>
            <h4>{getWordingForLanguage('upcomingApptTitle', props.selectedLanguageId)}</h4>
            <a style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              href={createEventString(new Date(props.user.appointmentDate!), new Date(props.user.appointmentDate!), `${getWordingForLanguage('upcomingApptTitle', props.selectedLanguageId)}`, `${getWordingForLanguage('apptScheduledDesc', props.selectedLanguageId)} ${getWordingForLanguage('with', props.selectedLanguageId)} ${props.user.assignedEmployeeName ?? ''}`, '#', props.user.assignedEmployeeStoreName)}>
              <CalendarMonthIcon style={{ color: '#1976d2' }} />
            </a>
          </div>
          <div className={styles.appointmentContent}>
            <div className={styles.appointmentRow}>
              <div className={styles.detailLabel}>{getWordingForLanguage('when', props.selectedLanguageId)}</div>
              <div className={styles.detailValue}>{new Date(props.user.appointmentDate).toLocaleDateString()} {new Date(props.user.appointmentDate).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}</div>
            </div>
            <div className={styles.appointmentRow}>
              <div className={styles.detailLabel}>{getWordingForLanguage('where', props.selectedLanguageId)}</div>
              <div className={styles.detailValue}>{props.user.assignedEmployeeStoreName}</div>
            </div>
            <div className={styles.appointmentRow}>
              <div className={styles.detailLabel}>{getWordingForLanguage('with', props.selectedLanguageId)}</div>
              <div className={styles.detailValue}>{props.user.assignedEmployeeName}</div>
            </div>
            {props.user.assignedEmployeeStorePhone && (
              <div className={styles.appointmentRow}>
                <div className={styles.detailLabel}>{getWordingForLanguage('callUs', props.selectedLanguageId)}</div>
                <div className={styles.detailValue}>{formatPhoneNumber(props.user.assignedEmployeeStorePhone)}</div>
              </div>
            )}
            {props.user.assignedEmployeeStoreAddress && (
              <div className={classNames(styles.appointmentRow, styles.noBorder, styles.extraPadding)}>
                <div className={classNames(styles.detailValue, styles.detailFullWidth)}>
                  <div>{props.user.assignedEmployeeStoreAddress}</div>
                </div>
              </div>
            )}

          </div>
          <div className={styles.appointmentActions}>
            <a className={styles.mapLink} href={getGoogleMapsUrl()} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
              <span>{getWordingForLanguage('openInMaps', props.selectedLanguageId)}</span>
              <div style={{ alignSelf: 'center' }}><MapIcon style={{ color: '#1976d2' }} /></div>
            </a>
          </div>
        </div>
      )}
      {isConfirmingAppointment === true && (
        <Dialog title={getWordingForLanguage('confirmAppt', props.selectedLanguageId)} closeIcon={true} onClose={() => setIsConfirmingAppointment(false)}>
          <p style={{ maxWidth: '300px', marginTop: 0 }}>
            {getConfirmApptDescription()}
          </p>
          <Button
            themeColor={'primary'}
            onClick={() => {
              if (isNotNil(selectedDate) && isNotNil(selectedTime)) {
                props.onScheduleAppointment(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), selectedTime.getHours(), selectedTime.getMinutes()));
                setIsConfirmingAppointment(false);
              }
            }}
          >
            {getWordingForLanguage('confirm', selectedLanguageId)}
          </Button>
          <Button
            themeColor={'base'}
            style={{ marginLeft: '10px' }}
            onClick={() => {
              setIsConfirmingAppointment(false);
            }}
          >
            {getWordingForLanguage('cancel', selectedLanguageId)}
          </Button>
        </Dialog>
      )}
      {props.isSchedulingAppointment === true && (
        <div style={{ margin: '10px 0' }}>
          <p>{getWordingForLanguage('apptScheduling', props.selectedLanguageId)}</p>
          <Loader type="pulsing" themeColor={'primary'} style={{ display: 'flex', justifyContent: 'center' }} />
        </div>
      )}
    </div>
  )
}